/**
 * Admin API Endpoints
 *
 * Endpoints for admin dashboard: query logs, analytics, and data export.
 * Protected by ACL (admin:platform role) via JWT auth.
 */

import { createLogger, type LogEntry } from '@/utils/logger';
import type { Page } from '../types';
import { randomUUID } from '@/lib/uuid';
import { api } from '../client';

const logger = createLogger('AdminAPI');


/**
 * Admin API GET (delegates to main api client with JWT auth)
 */
async function adminRequest<T>(path: string): Promise<T> {
  logger.debug(`Admin GET: ${path}`);
  const startTime = performance.now();

  try {
    const data = await api.get<T>(path);
    const duration = Math.round(performance.now() - startTime);

    logger.debug(`Admin GET response: ${path}`, {
      duration,
      dataKeys: data && typeof data === 'object' ? Object.keys(data) : [],
    });

    return data;
  } catch (error) {
    const duration = Math.round(performance.now() - startTime);
    logger.error(`Admin GET failed: ${path}`, { error, duration });
    throw error;
  }
}

/**
 * Admin API POST (delegates to main api client with JWT auth)
 */
async function adminRequestPost<T>(
  path: string,
  body: unknown,
  options?: { headers?: Record<string, string> },
): Promise<T> {
  logger.debug(`Admin POST: ${path}`);
  const startTime = performance.now();

  try {
    const data = await api.post<T>(path, body, options);
    const duration = Math.round(performance.now() - startTime);

    logger.debug(`Admin POST response: ${path}`, {
      duration,
      dataKeys: data && typeof data === 'object' ? Object.keys(data) : [],
    });

    return data;
  } catch (error) {
    const duration = Math.round(performance.now() - startTime);
    logger.error(`Admin POST failed: ${path}`, { error, duration });
    throw error;
  }
}

/**
 * Admin API PUT (delegates to main api client with JWT auth)
 */
async function adminRequestPut<T>(
  path: string,
  body: unknown,
  options?: { headers?: Record<string, string> },
): Promise<T> {
  logger.debug(`Admin PUT: ${path}`);
  const startTime = performance.now();

  try {
    const data = await api.put<T>(path, body, options);
    const duration = Math.round(performance.now() - startTime);

    logger.debug(`Admin PUT response: ${path}`, {
      duration,
      dataKeys: data && typeof data === 'object' ? Object.keys(data) : [],
    });

    return data;
  } catch (error) {
    const duration = Math.round(performance.now() - startTime);
    logger.error(`Admin PUT failed: ${path}`, { error, duration });
    throw error;
  }
}

/**
 * Admin API DELETE (delegates to main api client with JWT auth)
 */
async function adminRequestDelete<T>(path: string): Promise<T> {
  logger.debug(`Admin DELETE: ${path}`);
  const startTime = performance.now();

  try {
    const data = await api.delete<T>(path);
    const duration = Math.round(performance.now() - startTime);

    logger.debug(`Admin DELETE response: ${path}`, {
      duration,
      dataKeys: data && typeof data === 'object' ? Object.keys(data) : [],
    });

    return data;
  } catch (error) {
    const duration = Math.round(performance.now() - startTime);
    logger.error(`Admin DELETE failed: ${path}`, { error, duration });
    throw error;
  }
}

export interface QueryCluster {
  representative_query: string;
  count: number;
  example_queries: string[];
}

export interface DimensionGroup {
  dimension_value: string;
  clusters: QueryCluster[];
}

export interface QueryClusterResponse {
  dimension: string;
  groups: DimensionGroup[];
}

// --- Document Approval Types ---

// Single source of truth lives in endpoints/documents.ts; re-export so the
// admin-facing shape doesn't drift from the user-facing one. The pending-docs
// query filters to PENDING_APPROVAL + QUARANTINED,
// so the proto3 UNSPECIFIED zero value is unreachable here — the shared
// StatusBadge falls back to an "unknown" badge if that invariant ever breaks.
import { listDocuments } from './documents';
import type { DocumentStatus, DocumentCategory } from './documents';
export type { DocumentStatus, DocumentCategory };

// The approval queue is no longer a resource of its own: it is
// GET /documents?status=pending_approval,quarantined — a narrowing of the
// documents collection that also widens the row set for a caller who may
// approve. See listPendingDocuments below, which is now a thin call into
// listDocuments.

export interface FeedbackConfidence {
  tier: 'high' | 'medium' | 'low';
  score: number;
}

/**
 * One feedback entry — the ONE representation, from the item route, the
 * collection and a message's sub-collection alike. Mirrors the Go
 * FeedbackResponse.
 *
 * The nullable context fields are absent-vs-empty significant and every key is
 * always present: `tools_used` is null when the rated message has no
 * query_logs row at all (never recorded) and `[]` when a turn ran no tools.
 * The retired /admin/feedback query COALESCEd those together, which made an
 * unrecorded turn read as a tool-free one.
 *
 * `response_text` is deliberately absent: a feedback reader is not a transcript
 * reader.
 */
export interface FeedbackEntry {
  id: string;
  url: string;
  message_id: string;
  user_id: string;
  type: 'positive' | 'negative' | 'incorrect' | 'missing' | 'other';
  text: string | null;
  created_at: string;
  message_content: string | null;
  message_role: string | null;
  session_id: string | null;
  session_title: string | null;
  session_message_count: number | null;
  user_name: string | null;
  latency_ms: number | null;
  query_text: string | null;
  citation_count: number | null;
  tools_used: string[] | null;
  confidence: FeedbackConfidence | null;
}

/**
 * One page of feedback in the shared list envelope, plus the two aggregates the
 * explorer reads. `positive_count` and `negative_count` are computed over the
 * FILTERED set, never the page; the next page is the response's
 * `Link: rel="next"`, never inferred from page length.
 */
export interface FeedbackListResponse extends Page<FeedbackEntry> {
  positive_count: number;
  negative_count: number;
}

// --- Permission management (full-admin only) --------------------------------
// Wire shapes mirror the api-service-go /grants endpoints exactly
// (snake_case). A grant binds a principal (a user or a role) to a relation on a
// resource; the grantable vocabulary comes from /grants:targets so
// nothing here is hardcoded.

export type PermissionPrincipalType = 'user' | 'role';

// The full-platform-admin relation on system:platform. Value MUST equal
// `acl.RelationAdmin = "admin"` defined in tricorder-go/acl/openfga_writer.go —
// a rename there requires a paired rename here. The revoke-confirm guard keys
// on it; a shared const means a backend relation rename surfaces at this
// reference rather than silently disabling the last-admin lockout warning (the
// #1270 drift pattern). Lives here (not usePermissions) so component tests that
// mock the hooks module still see the real value.
export const RELATION_ADMIN = 'admin';

/** A single principal→relation binding on the selected resource. */
export interface PermissionGrant {
  principal_type: PermissionPrincipalType;
  principal_id: string;
  relation: string;
  /**
   * When this grant lapses on its own (RFC 3339). Set only for a CONDITIONED
   * tuple; absent means the grant is standing. A user inside the 30-day
   * new-user admin-view trial therefore holds TWO rows on system:platform — a
   * lapsing `admin_viewer` and whatever was granted deliberately — and only
   * this field tells them apart.
   */
  expires_at?: string;
}

/** Identifies what a grant applies to. */
export interface PermissionResourceRef {
  resource_type: string;
  resource_id: string;
}

/** Full grant body for POST grant/revoke. */
export interface PermissionGrantBody extends PermissionResourceRef, PermissionGrant {}

export interface ListPermissionsResponse extends PermissionResourceRef, Page<PermissionGrant> {}

/** An admin subresource a relation can be granted on (resource_type:"endpoint"). */
export interface PermissionEndpoint {
  id: string;
  name: string;
  /**
   * True when this area's rows carry cross-user, user-identifying content (raw
   * query text, a colleague's feedback, an error report with the reporter's id).
   * Display-only — the console shows a cue so granting it is deliberate rather
   * than an accident of a list where every row looks alike.
   */
  sensitive?: boolean;
}

/** One conversational agent as a grant target. */
export interface PermissionAgent {
  id: string;
  name: string;
  /**
   * Whether the agent is closed to the general population. Display-only: it
   * tells the admin whether a grant on this row CHANGES anything, so granting
   * is a deliberate widening rather than a shot in the dark.
   *
   * Optional because an api-service that predates the field omits it, and
   * `undefined` must not read as `false` — telling an admin that a restricted
   * agent is "open to everyone" is the exact misinformation this field exists
   * to prevent. Absent = say nothing.
   */
  restricted?: boolean;
}

/**
 * The grantable-target vocabulary — three families, each with model-derived
 * relations so the console can never offer a relation the grant handler would
 * reject:
 *
 *  - `endpoints`: admin subresources. Grant an `endpoint_relations` relation on
 *    resource_type:"endpoint", resource_id:id.
 *  - `agents`: conversational agents. Grant an `agent_relations` relation on
 *    resource_type:"agent", resource_id:slug — this IS how a user or role is
 *    given a restricted agent; there is no other gate.
 *  - the platform singleton (`admin_object`, resource_type:"system",
 *    resource_id:"platform"): `admin_relations` are the two admin tiers;
 *    `platform_relations` are the other delegable authorities (approver,
 *    contact_viewer) that govern dashboard areas with their own relation.
 *
 * `agents` / `agent_relations` / `platform_relations` are optional so a frontend
 * deployed ahead of its backend renders the families it does receive instead of
 * crashing on an absent key.
 */
export interface PermissionTargetsResponse {
  endpoint_relations: string[];
  endpoints: PermissionEndpoint[];
  agent_relations?: string[];
  agents?: PermissionAgent[];
  admin_object: string;
  admin_relations: string[];
  platform_relations?: string[];
}

// A client-side error a user tapped "Report" on (or an ErrorBoundary crash),
// persisted server-side and surfaced in the admin "Reported errors" tab.
export interface ClientErrorReport {
  id: string;
  user_id?: string;
  author_name?: string;
  context: string;
  message: string;
  error_code?: string;
  component_stack?: string;
  /** The redacted BROWSER path the error surfaced on ("/home"). */
  url?: string;
  /**
   * The API call that failed. Absent when the report was not about a call (an
   * ErrorBoundary render crash carries component_stack instead), and absent on
   * reports beaconed by clients older than this field — absent, never zeroed,
   * so a reader never mistakes "no request" for "status 0".
   */
  request_method?: string;
  request_path?: string;
  request_status?: number;
  /** X-Request-ID of the failed call — the join key to its server log line. */
  request_id?: string;
  /**
   * The conversation a failed turn happened in, and the seq of the message
   * that started it — the keys the triage view joins the session's records
   * and prompt by. Absent for a report about anything but a turn.
   */
  session_id?: string;
  turn_seq?: number;
  /** The pinned agent the turn ran on, as the reporting client held it. */
  agent_slug?: string;
  agent_version?: string;
  /** The browser's recent log ring, as it was beaconed — the console context
   *  the report carries so the failure does not have to be reproduced to be
   *  read. The entry type is the logger's own (utils/logger LogEntry): one
   *  type for the producer and the reader, so the wire shape cannot drift
   *  between them. */
  console_log?: LogEntry[];
  created_at: string;
}

export type ClientErrorReportsResponse = Page<ClientErrorReport>;

/** A principal: a person, an agent, or an outbound channel. */
export interface TriagePrincipal {
  type: 'user' | 'agent' | 'channel';
  id: string;
}

export type TriageStatus = 'open' | 'in_progress' | 'done' | 'cancelled';

/**
 * Who owns a report right now — FOLDED from the event log on read, never stored.
 * There is no assignment row to drift from the history.
 */
export interface ReportAssignment {
  assignee: TriagePrincipal;
  assigned_by: TriagePrincipal;
  status: TriageStatus;
  updated_at: string;
}

/** One entry of the append-only triage history. */
export interface TriageEvent {
  id: string;
  event_type: 'assigned' | 'status_changed' | 'unassigned';
  actor: TriagePrincipal;
  assignee_type?: TriagePrincipal['type'];
  assignee_id?: string;
  status?: TriageStatus;
  created_at: string;
}

export interface ReportComment {
  id: string;
  author: TriagePrincipal;
  body: string;
  resolved: boolean;
  created_at: string;
}

/** An assignable outbound sink — name and kind only; the URL never leaves the server. */
export interface TriageChannelOption {
  name: string;
  kind: string;
}

export interface ReportTriageResponse {
  report: ClientErrorReport;
  assignment?: ReportAssignment;
  events: TriageEvent[];
  /** The first page of the thread, in the same body the stand-alone
   *  GET /client-errors/{id}/comments answers with. */
  comments: Pick<Page<ReportComment>, 'items'>;
  channels: TriageChannelOption[];
  /** The server side of the failure, folded in from the session service when
   *  the report names a conversation. Each piece carries its own error when
   *  its read failed — a refused read degrades visibly, never silently. */
  diagnostics?: ReportDiagnostics;
}

/** What happened under the hood: the conversation, the turn's own records
 *  (its payload, its turn_failure record), and the rendered prompt. */
export interface ReportDiagnostics {
  session?: ReportSessionSummary;
  turn?: ReportTurnRecords;
  prompt?: ReportPromptRender;
  unavailable?: string;
}

export interface ReportSessionSummary {
  id: string;
  agent?: { slug: string; version: string };
  subject?: { type: string; id: string };
  title?: string;
  state?: string;
  created_at?: string;
  error?: string;
}

export interface ReportTurnRecords {
  seq: number;
  records: ReportSessionRecord[];
  error?: string;
}

export interface ReportSessionRecord {
  id: string;
  seq: number;
  type?: string;
  role?: string;
  author?: { performer: string; on_behalf_of?: string };
  /** The record's payload in its own schema — an object for a turn_failure,
   *  a ContentBlock ARRAY for a conversation record, so unknown, not a
   *  record type that would lie about one of the two. */
  payload?: unknown;
  created_at?: string;
}

export interface ReportPromptRender {
  rendered?: string;
  layers?: { name: string; version: string }[];
  error?: string;
}

/** The one triage write. `status` is required for status_changed, optional on assigned. */
export interface TriageEventRequest {
  event_type: TriageEvent['event_type'];
  assignee_type?: TriagePrincipal['type'];
  assignee_id?: string;
  status?: TriageStatus;
}

export const adminApi = {
  /**
   * Feedback is ONE collection at ONE address. /admin/feedback was a second
   * name for it that read a different table and so returned a different set
   * for the same question; the gate was identical either way.
   */
  getClientErrors: (limit = 100, offset = 0) =>
    api.getPage<ClientErrorReportsResponse>(`/client-errors?limit=${limit}&offset=${offset}`),

  /** One report with its current assignment, history, thread and channel options. */
  getClientErrorTriage: (reportId: string) =>
    adminRequest<ReportTriageResponse>(`/client-errors/${reportId}`),

  /**
   * Append a triage event. This is the ONLY write for assignment and status:
   * they are things that happened, not fields to overwrite, so the server keeps
   * the whole history and derives "who owns it now" from it.
   */
  postClientErrorEvent: (reportId: string, body: TriageEventRequest) =>
    adminRequestPost<{ event: TriageEvent; assignment?: ReportAssignment }>(
      `/client-errors/${reportId}/events`,
      body,
      // client.ts replays the same headers verbatim on a 503 cold-start retry.
      // Without this key that replay appends a SECOND event to an append-only
      // log — the server-side support is a no-op when the header is absent.
      { headers: { 'Idempotency-Key': randomUUID() } },
    ),

  /** Reply on a report's thread — the "it's fixed" the reporter never got. */
  postClientErrorComment: (reportId: string, body: string) =>
    adminRequestPost<ReportComment>(
      `/client-errors/${reportId}/comments`,
      { body },
      { headers: { 'Idempotency-Key': randomUUID() } },
    ),

  // --- The access graph -----------------------------------------------------
  //
  // One collection, /grants. These were /admin/permissions/*, where `admin`
  // named who may call rather than what is addressed; authority is now derived
  // from the target inside the handler. Only the vocabulary below stays
  // platform-admin gated, because it is a map of the deployment's internals.

  // The grantable-target vocabulary (endpoints + admin tiers + their relations).
  getPermissionTargets: () => adminRequest<PermissionTargetsResponse>('/grants:targets'),

  // Who can reach one resource (principal_type/principal_id/relation rows).
  listPermissions: (resourceType: string, resourceId: string) => {
    const q = new URLSearchParams({ resource_type: resourceType, resource_id: resourceId });
    return api.getPage<ListPermissionsResponse>(`/grants?${q.toString()}`);
  },

  // Bind a principal to a relation on a resource. Per-call Idempotency-Key so a
  // bedside network retry can't double-apply the grant (same convention as
  // approveDocument / org writes); the server replays the cached 201.
  grantPermission: (body: PermissionGrantBody) =>
    adminRequestPost<unknown>('/grants', body, {
      headers: { 'Idempotency-Key': randomUUID() },
    }),

  // Remove a binding. A grant has no server-assigned id — an OpenFGA tuple IS
  // its (principal, relation, resource) — so the tuple travels in the query
  // string rather than as five path segments carrying dotted ids.
  revokePermission: (body: PermissionGrantBody) => {
    const q = new URLSearchParams({
      resource_type: body.resource_type,
      resource_id: body.resource_id,
      relation: body.relation,
      principal_type: body.principal_type,
      principal_id: body.principal_id,
    });
    return adminRequestDelete<unknown>(`/grants?${q.toString()}`);
  },

  listPendingDocuments: (limit = 100, offset = 0) =>
    listDocuments(limit, offset, ['pending_approval', 'quarantined']),

  /**
   * Writes one approver's sign-off. PUT, because document_approvals' key is
   * (document_id, approver_id) — the client knows the row's URI, so a retry is
   * the same request, not a duplicate.
   *
   * 201 when this call created the row, 200 when it was already there. Neither
   * means "the document is approved": reaching the quorum is what flips it, and
   * the response body carries approvals/quorum so the caller can tell.
   */
  putDocumentApproval: (documentId: string, approverId: string) =>
    adminRequestPut<{ document_id: string; approver_id: string; approvals: number; quorum: number }>(
      `/documents/${documentId}/approvals/${approverId}`,
      {},
      // Per-call Idempotency-Key — a bedside network retry must not re-count the
      // approval quorum (the handler skips dedup entirely when the header is
      // absent). Same convention as documents.ts / bookmarks.ts.
      { headers: { 'Idempotency-Key': randomUUID() } }
    ),

  deleteDocument: (documentId: string) =>
    adminRequestDelete<{ success: boolean }>(
      `/documents/${documentId}`,
    ),
};

